import React from 'react';
import _ from 'lodash';
import { googleOauth } from 'customer/context';
import { useHistory } from 'react-router-dom';
import copilot from '@janus.team/janus-copilot/cjs/janus-copilot';
import Stepper from './stepper/index';
import Step from './stepper/Step';
import FinishSetup from './steps/FinishSetup/index';
import { useQueryModifyPolicy, useQueryClaimJanusOrg } from '../../hooks/queries';
import { useSignout } from '../../hooks/signout';
import LinkOrganizationContent from './steps/LinkOrganization/Content';
import LinkOrganizationStatus from './steps/LinkOrganization/Status';
import RackspacePermissionsContent from './steps/RackspacePermissions/Content';
import RackspacePermissionsStatus from './steps/RackspacePermissions/Status';
import { useNotificationToaster } from '../../notifications/api';

const STEP_INDEX = {
  SELECT_ORG: 0,
  GRANTED_ROLES: 1,
  FINISH: 2,
};

const JANUS_ADMINS_ROLE = process.env.REACT_APP_ENVIRONMENT === 'prod'
  ? 'rax-aws-saml-janus-prod-admins'
  : 'rax-aws-saml-janus-dev-admins';
const REQUIRED_ROLES = {
  'identity:user-admin': 'Account Administrator',
  'gcp:account-creator': 'Account Creator for GCP',
};

const OnBoarding = () => {
  const history = useHistory();
  const { mutate: mutateOrgPolicy } = useQueryModifyPolicy();
  const { mutate: mutateJanusClaimOrg } = useQueryClaimJanusOrg();
  const { setGoogleOauthStatus } = React.useContext(googleOauth) || {};
  const { signOut } = useSignout(setGoogleOauthStatus);
  const { notify } = useNotificationToaster();
  const { session } = React.useContext(copilot.context.session);

  const userRoles = _.get(session, 'session.identity.access.user.roles', []);
  const userRoleNames = userRoles.map((role) => role.name);
  const hasRequiredRole = userRoleNames.some((role) => Object.keys(REQUIRED_ROLES).includes(role) || role === JANUS_ADMINS_ROLE);

  if (!hasRequiredRole) {
    return (
      <div className="container mt-10" style={{ maxWidth: '600px' }}>
        <div className="text-center mb-5">
          <i className="fa fa-exclamation-triangle fa-3x text-warning" />
        </div>
        <h4 className="alert-heading text-center">Insufficient Permissions</h4>
        <p>
          You do not have the necessary permissions to complete the onboarding of your GCP Organization.
          To proceed, you need one of the following roles:
        </p>
        <ul>
          {Object.entries(REQUIRED_ROLES).map(([role, name]) => (
            <li key={role}><code>{name}</code></li>
          ))}
        </ul>
        <hr />
        <p className="mb-0">
          Please contact your Account Manager / Technical Account Manager to request the appropriate permissions.
        </p>
      </div>
    );
  }

  const handleFinished = async (stepResults) => {
    const { googleOrganization } = stepResults[STEP_INDEX.SELECT_ORG];
    const selectedRoles = _.pickBy(stepResults[STEP_INDEX.GRANTED_ROLES].roles);
    const constraints = _.keys(_.pickBy(_.get(stepResults[STEP_INDEX.GRANTED_ROLES].constraints)));

    const orgdata = {
      googleOrganization,
      selectedRoles,
      constraints,
    };
    mutateJanusClaimOrg(orgdata, {
      onSuccess: async () => {
        if (_.isEmpty(selectedRoles)) return window.location.reload();
        mutateOrgPolicy(orgdata, {
          onSettled: () => {
            history.push('/');
            window.location.reload();
          },
        });
      },
      onError: async (error) => {
        notify({ category: 'error', title: 'Not able to claim GCP organization', details: error.message });
      },
    });
  };

  const handleError = () => {
    alert('Error logic need to be implemented'); // eslint-disable-line no-alert
  };

  return (
    <Stepper
      handleFinished={handleFinished}
      handleLogout={signOut}
      handleError={handleError}
    >
      <Step
        title="Link a Google Organization"
        isValid={_.negate(_.isNil)}
        Content={LinkOrganizationContent}
        Status={LinkOrganizationStatus}
      />
      <Step
        title="Rackspace Permissions"
        isValid={(result) => !result?.loading}
        Content={RackspacePermissionsContent}
        Status={RackspacePermissionsStatus}
      />
      <Step
        title="Finish Setup"
        Content={FinishSetup}
        isValid={(result) => result && !!result.finished}
        Status={() => { }}
      />
    </Stepper>
  );
};

export default OnBoarding;
